<script setup lang="ts">
import LargeWideModel from '@/components/Interface/Modal/ModelSizes/LargeWideModel.vue'
import FerionDataTable from '@/components/Interface/DataTable/FerionDataTable.vue'
import HeaderTextFlex from '@/components/Interface/DataTable/Headers/HeaderTextFlex.vue'
import HeaderFixedWidthText from '@/components/Interface/DataTable/Headers/HeaderFixedWidthText.vue'
import CellFlexWidth from '@/components/Interface/DataTable/Cell/CellFlexWidth.vue'
import CellFixedWidth from '@/components/Interface/DataTable/Cell/CellFixedWidth.vue'
import StandardCard from '@/components/Elements/Cards/StandardCard.vue'
import PositionIcon from '@/components/Elements/Images/Icons/PositionIcon.vue'
import SimpleProgressBar from '@/components/Elements/ProgressBar/SimpleProgressBar.vue'
import ToggleSwitch from '@/components/Elements/ToggleSwitch/ToggleSwitch.vue'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import StyledImage from '@/components/Elements/Images/StyledImage.vue'
import { usePlanetStore } from '@/stores/planetStore'
import { useGameStore } from '@/stores/gameStore'
import { useAssetStore } from '@/stores/assetStore'
import { usePlayerOreStore } from '@/stores/playerOreStore'
import { useTableStore } from '@/stores/tableStore'
import { computed, onMounted } from 'vue'
import Backend from '@/models/backend'
import { CDN } from '@/models/cdn.ts'
import { logger } from '@/models/logger'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const planetStore = usePlanetStore()
const gameStore = useGameStore()
const assetStore = useAssetStore()
const playerOreStore = usePlayerOreStore()
const tableStore = useTableStore()
const backend = new Backend()

// Clear any row selection when modal opens
onMounted(() => {
  tableStore.ringworldConversionStars.singleSelectedId = 0
})

const starsWithNonRingworldPlanets = computed(() => {
  const stars = planetStore.getStarsWithNonRingworldPlanets()
  // Convert array to Map for FerionDataTable compatibility, using starId as key
  const starsMap = new Map()
  stars.forEach((star) => {
    // Add a sortable coordinates field for better sorting and required id field
    const starWithSortableLocation = {
      ...star,
      id: star.starId, // Add id field required by FerionDataTable
      locationX: star.location.x,
      locationY: star.location.y,
      coordinatesText: `(${star.location.x}, ${star.location.y})`
    }
    starsMap.set(star.starId, starWithSortableLocation)
  })
  return starsMap
})

// Computed property to determine toggle state for each star
const getToggleState = (item: any) => {
  const planets = item.planetIds.map((id: number) => planetStore.findPlanetById(id)).filter(Boolean)
  const enabledCount = planets.filter((planet: any) => planet.ringworldConversionEnabled > 0).length
  
  if (enabledCount === 0) return false
  if (enabledCount === planets.length) return true
  return 'mixed' // Some enabled, some disabled
}

// Toggle handler function
const handleToggle = async (item: any) => {
  const planets = item.planetIds.map((id: number) => planetStore.findPlanetById(id)).filter(Boolean)
  const enabledCount = planets.filter((planet: any) => planet.ringworldConversionEnabled > 0).length
  
  // Determine which planets to toggle based on the requirements:
  // - If only some planets are enabled (mixed state), disable all enabled ones
  // - If all are disabled, enable all
  // - If all are enabled, disable all
  let planetsToToggle: number[]
  
  if (enabledCount === 0) {
    // All disabled - enable all
    planetsToToggle = item.planetIds
  } else if (enabledCount === planets.length) {
    // All enabled - disable all
    planetsToToggle = item.planetIds
  } else {
    // Mixed state - disable all enabled ones
    planetsToToggle = planets
      .filter((planet: any) => planet.ringworldConversionEnabled > 0)
      .map((planet: any) => planet.id)
  }
  
  try {
    await backend.toggleRingworldConversion(planetsToToggle)
  } catch (error) {
    logger.error('Failed to toggle ringworld conversion:', error)
  }
}

// Get the required ore for ringworld conversion
const ringworldOre = computed(() => {
  const oreId = gameStore.player?.ringworldOreId
  if (!oreId) return null
  return assetStore.findOreById(oreId)
})

// Calculate total ore needed for all conversions
const totalOreRequired = computed(() => {
  let total = 0
  const orePerPlanet = gameStore.ringworldOrePerPlanet || 3
  
  starsWithNonRingworldPlanets.value.forEach((star) => {
    // For each planet in the star that needs conversion
    const planets = star.planetIds.map((id: number) => planetStore.findPlanetById(id)).filter(Boolean)
    planets.forEach((planet: any) => {
      const remaining = orePerPlanet - planet.ringworldConversionProgress
      if (remaining > 0) {
        total += remaining
      }
    })
  })
  
  return total
})

// Get current ore amount
const currentOreAmount = computed(() => {
  if (!ringworldOre.value) return 0
  
  const oreCode = ringworldOre.value.code
  if (!oreCode) return 0
  
  return playerOreStore.getOreStored(oreCode)
})

// Get the player's home world planet type name
const homeWorldPlanetTypeName = computed(() => {
  const planetTypeId = gameStore.player?.homeWorldPlanetType
  if (!planetTypeId) return 'Planets'
  const planetType = assetStore.findPlanetTypeById(planetTypeId)
  return planetType?.name || 'Planets'
})

// Simple filter function that returns all stars as an array
const filters = (stars: any[]) => {
  return stars
}

</script>

<template>
  <LargeWideModel :title="t('modals.ringworldConversion.title')">
    <StandardCard top-padding="2" bottom-padding="2" class="px-2">
      <div class="flex flex-col sm:flex-row text-center sm:text-left">
        <StyledImage
          class="m-2 self-center sm:self-start"
          :src="CDN.techIcon('ringworlds')"
          alt="Ringworld Technology"
          size="md"
        />
        <div class="text-subtext p-2">
          <div class="pb-2">
            <p class="pb-1">{{ t('modals.ringworldConversion.description') }}</p>
            <ul class="text-metal-300 list-disc list-inside">
              <li>{{ t('modals.ringworldConversion.requirements.ownAllPlanets') }} <span class="text-subtext">{{ t('modals.ringworldConversion.requirements.ownAllPlanetsNote') }}</span></li>
              <li>{{ t('modals.ringworldConversion.requirements.planetTypePart1') }} {{ homeWorldPlanetTypeName }} {{ t('modals.ringworldConversion.requirements.planetTypePart2') }} <span class="text-subtext">({{ homeWorldPlanetTypeName }} {{ t('modals.ringworldConversion.requirements.planetTypeNote') }}</span></li>
              <li>{{ t('modals.ringworldConversion.requirements.oreRequiredPart1') }} {{ gameStore.ringworldOrePerPlanet || 3 }} <GenericIcon v-if="ringworldOre" :type="'ore' + ringworldOre.id" :tooltip="ringworldOre.oreName" /> {{ ringworldOre?.oreName || 'ore' }} {{ t('modals.ringworldConversion.requirements.oreRequiredPart2') }} <span class="text-subtext">{{ t('modals.ringworldConversion.requirements.oreNote') }}</span></li>
            </ul>
          </div>
          <div v-if="ringworldOre">
            {{ t('modals.ringworldConversion.oreStatusPart1') }} <span class="text-metal-200">{{ totalOreRequired.toLocaleString() }}</span> <GenericIcon :type="'ore' + ringworldOre.id" :tooltip="ringworldOre.oreName" /> {{ ringworldOre.oreName }}, {{ t('modals.ringworldConversion.oreStatusPart2') }} <span class="text-metal-200">{{ currentOreAmount.toLocaleString() }}</span> {{ t('modals.ringworldConversion.oreStatusPart3') }} <span class="text-metal-200">{{ gameStore.ringworldOrePerPlanet || 3 }}</span> <GenericIcon :type="'ore' + ringworldOre.id" :tooltip="ringworldOre.oreName" /> {{ ringworldOre.oreName }} {{ t('modals.ringworldConversion.oreStatusPart4') }}
          </div>
          <div v-else>
            {{ t('modals.ringworldConversion.noOre') }}
          </div>
        </div>
      </div>
    </StandardCard>

    <div v-if="starsWithNonRingworldPlanets.size === 0" class="p-4">
      <div class="text-metal-300 text-center bg-metal-800 rounded-lg p-6">
        <div class="text-lg mb-2">{{ t('modals.ringworldConversion.noStars.title') }}</div>
        <div class="text-sm text-metal-400">
          {{ t('modals.ringworldConversion.noStars.description') }}
        </div>
      </div>
    </div>

    <FerionDataTable
      v-else
      :data="starsWithNonRingworldPlanets"
      :filters="filters"
      tableStoreName="ringworldConversionStars"
      :selector="false"
      :footerBar="true"
      :heightLimit="'h-[calc(100vh-25rem)]'"
      :click="() => {}"
      :doubleClick="() => {}"
      class="m-2 pb-2"
    >
      
      <template #columns="{ sort, sortColumn, sortDirection }">
        <HeaderTextFlex
          min-width="16"
          column="name"
          :sortColumn="sortColumn"
          :sortDirection="sortDirection"
          @sort="sort"
          class="pl-2"
        >
          {{ t('modals.ringworldConversion.columns.starName') }}
        </HeaderTextFlex>
        <HeaderFixedWidthText
          class="rounded-tr pr-2"
          width="4"
          column="locationX"
          :sortColumn="sortColumn"
          :sortDirection="sortDirection"
          @sort="sort"
          align="center"
        >
          {{ t('modals.ringworldConversion.columns.position') }}
        </HeaderFixedWidthText>
        <HeaderFixedWidthText
          width="10"
          column="totalPopulation"
          :sortColumn="sortColumn"
          :sortDirection="sortDirection"
          @sort="sort"
          align="center"
        >
          {{ t('modals.ringworldConversion.columns.population') }}
        </HeaderFixedWidthText>
        <HeaderFixedWidthText
          width="10"
          column="nonRingworldCount"
          :sortColumn="sortColumn"
          :sortDirection="sortDirection"
          @sort="sort"
          align="center"
        >
          {{ t('modals.ringworldConversion.columns.planetsOwned') }}
        </HeaderFixedWidthText>
        <HeaderFixedWidthText
          width="10"
          column="homeWorldTypeCount"
          :sortColumn="sortColumn"
          :sortDirection="sortDirection"
          @sort="sort"
          align="center"
        >
          {{ homeWorldPlanetTypeName }} Planets
        </HeaderFixedWidthText>
        <HeaderFixedWidthText
          width="12"
          column="planetsConverting"
          :sortColumn="sortColumn"
          :sortDirection="sortDirection"
          @sort="sort"
          align="center"
        >
          {{ t('modals.ringworldConversion.columns.planetsConverting') }}
        </HeaderFixedWidthText>
        <HeaderFixedWidthText
          width="20"
          column="totalConversionProgress"
          :sortColumn="sortColumn"
          :sortDirection="sortDirection"
          @sort="sort"
          align="left"
        >
          {{ t('modals.ringworldConversion.columns.conversionProgress') }}
        </HeaderFixedWidthText>
        <HeaderFixedWidthText
          class="rounded-tr pr-2"
          width="10"
          column="planetsConverting"
          :sortColumn="sortColumn"
          :sortDirection="sortDirection"
          @sort="sort"
          align="center"
        >
          {{ t('modals.ringworldConversion.columns.toggleAll') }}
        </HeaderFixedWidthText>

      </template>
      
      <template #rows="{ item }">
        <CellFlexWidth min-width="16" class="pl-2">
          <div class="text-metal-200 font-medium">{{ item.name }}</div>
        </CellFlexWidth>

        <CellFixedWidth width="4" align="center" class="pr-2">
          <PositionIcon :x="item.location.x" :y="item.location.y" />
        </CellFixedWidth>

        <CellFixedWidth width="10" align="center">
          <div class="text-metal-200">
            {{ item.totalPopulation.toLocaleString() }}
          </div>
        </CellFixedWidth>

        <CellFixedWidth width="10" align="center">
          <div :class="item.nonRingworldCount === 5 ? 'text-green-400' : 'text-red-400'">
            {{ item.nonRingworldCount }} / 5
          </div>
        </CellFixedWidth>
        
        <CellFixedWidth width="10" align="center">
          <div :class="item.homeWorldTypeCount === 5 ? 'text-green-400' : item.homeWorldTypeCount > 0 ? 'text-yellow-400' : 'text-red-400'">
            {{ item.homeWorldTypeCount }} / 5
          </div>
        </CellFixedWidth>
        
        <CellFixedWidth width="12" align="center">
          <div :class="item.planetsConverting === 5 ? 'text-green-400' : 'text-red-400'">
            {{ item.planetsConverting }} / 5
          </div>
        </CellFixedWidth>
        
        <CellFixedWidth width="20" align="left">
          <SimpleProgressBar
            :percentage="(item.totalConversionProgress / item.maxConversionProgress) * 100"
            :text="`${Math.round((item.totalConversionProgress / item.maxConversionProgress) * 100)}%`"
            :subtext="`${item.totalConversionProgress} / ${item.maxConversionProgress}`"
            color="purple"
            :height="20"
          />
        </CellFixedWidth>
        
        <CellFixedWidth width="10" align="center" class="pr-2 mb-2">
          <ToggleSwitch 
            :modelValue="getToggleState(item) === true"
            @update:modelValue="handleToggle(item)"
          />
        </CellFixedWidth>

      </template>
    </FerionDataTable>
  </LargeWideModel>
</template>

<style scoped lang="scss">

</style>